import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Apple, AlertCircle, CheckCircle2, Save, Loader2, Heart, Globe, Utensils, Plus, X, Calendar, ChefHat, Clock } from 'lucide-react';
import { authFetch } from '../lib/authFetch';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { fortnightForWeek, mealsForFortnight, fortnightWeekLabel, FORTNIGHT_THEMES, TOTAL_FORTNIGHTS } from '../data/dietRotation';
import { localizedTitle } from '../data/mealTitleTranslations';
import { imageForMeal } from '../data/mealImages';
import useTheme4A from '../components/useTheme4A';
import useAllowPrint from '../components/useAllowPrint';

const PREF_GROUPS = [
  {
    title: 'Diet pattern',
    options: [
      { key: 'vegetarian', label: 'Vegetarian (no meat or fish)' },
      { key: 'vegan', label: 'Vegan (no animal products)' },
      { key: 'gluten_free', label: 'Gluten-free' },
      { key: 'lactose_free', label: 'Lactose-free / dairy-free' },
      { key: 'low_carb', label: 'Low-carb' },
      { key: 'low_sodium', label: 'Low-sodium' },
    ],
  },
  {
    title: 'Cultural / religious',
    options: [
      { key: 'halal', label: 'Halal' },
      { key: 'kosher', label: 'Kosher' },
      { key: 'no_pork', label: 'No pork' },
      { key: 'no_beef', label: 'No beef' },
      { key: 'no_shellfish', label: 'No shellfish' },
    ],
  },
  {
    title: 'Medical',
    options: [
      { key: 'diabetic', label: 'Diabetic / blood-sugar focused' },
    ],
  },
];

const COMMON_ALLERGIES = ['Peanut', 'Tree nuts', 'Egg', 'Milk', 'Soy', 'Wheat', 'Fish', 'Shellfish', 'Sesame', 'Mustard'];

// Cuisine preferences — these map to the `country` field on each meal so the
// Diet tab filter can show only meals from the user's preferred cuisines.
const CUISINE_OPTIONS = [
  { id: 'western', label: 'Western', flag: '🥗' },
  { id: 'italy', label: 'Italian', flag: '🇮🇹' },
  { id: 'france', label: 'French', flag: '🇫🇷' },
  { id: 'spain', label: 'Spanish', flag: '🇪🇸' },
  { id: 'portugal', label: 'Portuguese', flag: '🇵🇹' },
  { id: 'greece', label: 'Greek', flag: '🇬🇷' },
  { id: 'russia', label: 'Russian', flag: '🇷🇺' },
  { id: 'ukraine', label: 'Ukrainian', flag: '🇺🇦' },
  { id: 'korea', label: 'Korean', flag: '🇰🇷' },
  { id: 'india', label: 'Indian', flag: '🇮🇳' },
  { id: 'mexico', label: 'Mexican', flag: '🇲🇽' },
  { id: 'asia', label: 'Other Asian', flag: '🌏' },
  { id: 'middle-east', label: 'Middle Eastern', flag: '🌗' },
];

export default function DietRecoveryPage() {
  useAllowPrint();
  useTheme4A();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language } = useLanguage();
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const currentWeek = user?.current_week ?? -2;
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'beta_tester';
  const inWindow = !isSuperAdmin && currentWeek <= 0;
  // Effective fortnight: SuperAdmin always sees fortnight 1; others use current week
  const fortnight = isSuperAdmin ? 1 : (fortnightForWeek(currentWeek) || 1);
  const featured = mealsForFortnight(fortnight);

  const [prefs, setPrefs] = useState({
    vegetarian: false, vegan: false, gluten_free: false, lactose_free: false,
    halal: false, kosher: false, no_pork: false, no_beef: false, no_shellfish: false,
    diabetic: false, low_sodium: false, low_carb: false,
    allergies: [], cuisines: [], dislikes: [], spicy_preference: 'medium', notes: ''
  });
  const [completed, setCompleted] = useState(false);
  const [allergyInput, setAllergyInput] = useState('');
  const [dislikeInput, setDislikeInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await authFetch(`${apiBase}/api/user/diet-preferences`);
        if (res.ok) {
          const data = await res.json();
          if (data.preferences) {
            setPrefs(p => ({ ...p, ...data.preferences }));
            setCompleted(!!data.completed);
          }
        }
      } catch (e) { /* ignore */ }
      setLoading(false);
    })();
  }, [apiBase]);

  const toggle = (key) => setPrefs(p => ({ ...p, [key]: !p[key] }));

  const addAllergy = (val) => {
    const a = (val || '').trim();
    if (!a) return;
    if (prefs.allergies.includes(a)) return;
    setPrefs(p => ({ ...p, allergies: [...p.allergies, a] }));
    setAllergyInput('');
  };

  const removeAllergy = (val) => setPrefs(p => ({ ...p, allergies: p.allergies.filter(a => a !== val) }));

  // Cuisines: multi-select toggle
  const toggleCuisine = (id) => setPrefs(p => {
    const current = p.cuisines || [];
    return { ...p, cuisines: current.includes(id) ? current.filter(c => c !== id) : [...current, id] };
  });

  // Dislikes: free-text chips (similar to allergies)
  const addDislike = (val) => {
    const a = (val || '').trim();
    if (!a) return;
    const current = prefs.dislikes || [];
    if (current.includes(a)) return;
    setPrefs(p => ({ ...p, dislikes: [...(p.dislikes || []), a] }));
    setDislikeInput('');
  };
  const removeDislike = (val) => setPrefs(p => ({ ...p, dislikes: (p.dislikes || []).filter(d => d !== val) }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${apiBase}/api/user/diet-preferences`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: prefs }),
      });
      if (res.ok) {
        setCompleted(true);
        toast.success('Dietary preferences saved');
      } else {
        toast.error('Could not save preferences');
      }
    } catch (e) {
      toast.error('Network error');
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-900 mb-4 text-sm"
          data-testid="diet-recovery-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <header className="mb-6">
          <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3">
            <Apple className="w-3.5 h-3.5" />
            Diet &amp; Recovery
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-zinc-900 mb-3">A daily Recovery tool</h1>
          <p className="text-sm text-zinc-600 leading-relaxed">
            Good food is one of the most under-rated tools in long-term Recovery. Years of substance use deplete vitamins, minerals and amino acids, damage the gut, stress the liver and rewire the reward system around fast, refined sugars. Rebuilding a varied, nourishing diet directly supports the brain, the body and the steady mood that protects sobriety.
          </p>
        </header>

        {/* Why this matters */}
        <section className="bg-white border border-zinc-200 rounded-xl p-5 mb-5">
          <h2 className="text-base font-bold text-zinc-900 mb-3 flex items-center gap-2">
            <Heart className="w-4 h-4 text-rose-500" />
            Why a healthy diet matters in Recovery
          </h2>
          <ul className="space-y-2 text-sm text-zinc-700">
            <li className="flex gap-2"><span className="text-emerald-500 font-bold">•</span><span><strong>Stable blood sugar</strong>: the swings of refined sugar mimic the irritability and restlessness that drive cravings. Whole foods even out the highs and lows.</span></li>
            <li className="flex gap-2"><span className="text-emerald-500 font-bold">•</span><span><strong>Mood-supporting nutrients</strong>: B-vitamins, magnesium, omega-3, zinc and folate are commonly depleted in long-term substance use and are critical for serotonin, dopamine and GABA function.</span></li>
            <li className="flex gap-2"><span className="text-emerald-500 font-bold">•</span><span><strong>Gut-brain axis</strong>: about 90% of your serotonin is made in the gut. Probiotics (kimchi, yoghurt, kefir) and fibre feed the bacteria that feed your mood.</span></li>
            <li className="flex gap-2"><span className="text-emerald-500 font-bold">•</span><span><strong>Liver and tissue repair</strong>: protein and antioxidants give the body the raw materials it needs to undo years of metabolic stress.</span></li>
            <li className="flex gap-2"><span className="text-emerald-500 font-bold">•</span><span><strong>Structure and ritual</strong>: pre-committed meals, regular shopping and weekly cooking restore the daily structure that addiction strips away.</span></li>
          </ul>
        </section>

        {/* Variety */}
        <section className="bg-white border border-zinc-200 rounded-xl p-5 mb-5">
          <h2 className="text-base font-bold text-zinc-900 mb-3 flex items-center gap-2">
            <Globe className="w-4 h-4 text-blue-500" />
            Variety is the goal
          </h2>
          <p className="text-sm text-zinc-700 leading-relaxed mb-2">
            The Diet Library is intentionally diverse. Boredom is one of the strongest known relapse triggers and the same dull plate three times a day is a setup for the brain to start chasing chemical novelty again.
          </p>
          <p className="text-sm text-zinc-700 leading-relaxed mb-2">
            We build the library across <strong>multiple cuisines</strong>: Western, Italian, French, Spanish, Portuguese, Russian, Ukrainian, Korean: so participants in every language we support find food that tastes like home, plus enough new options to keep the table interesting. We also balance <strong>vegetarian and meat-based</strong> meals and clearly tag gluten-free, vegan, high-protein and budget options.
          </p>
          <p className="text-sm text-zinc-700 leading-relaxed">
            New recipes are added every two weeks. Use the <strong>Save to my plan</strong> button on any meal to schedule it into your week, then print the auto-generated shopping list before you head out.
          </p>
        </section>

        {/* Social ritual */}
        <section className="bg-white border border-zinc-200 rounded-xl p-5 mb-5">
          <h2 className="text-base font-bold text-zinc-900 mb-3 flex items-center gap-2">
            <span className="text-blue-600">🤝</span>
            The Recovery social ritual
          </h2>
          <p className="text-sm text-zinc-700 leading-relaxed mb-2">
            Active addiction isolates. By the time many of us reach Recovery, the small repeated rituals that bind us to family, friends and community have eroded: replaced by drinking partners, dealers or the closed-off solitude of using alone.
          </p>
          <p className="text-sm text-zinc-700 leading-relaxed mb-2">
            Rebuilding those rituals is one of the strongest evidence-based protectors of long-term sobriety. Shared cooking is one of the easiest places to start. The chopping, stirring and waiting all become unhurried moments of connection: exactly the slow time you used to spend in pubs and bars. It costs almost nothing, has no entry barrier and reliably ends in a memory worth keeping.
          </p>
          <p className="text-sm text-zinc-700 leading-relaxed">
            Inside the Diet Library, every recipe has a <strong>"Cook with a Recovery friend"</strong> button. It generates a sober dinner-invitation message you can text or paste: to your sponsor, a Recovery peer, a family member or a sober friend. Try it once a week. Small rituals, repeated, are how Recovery is built.
          </p>
        </section>

        {/* This fortnight's featured recipes */}
        <section className="bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200 rounded-xl p-5 mb-5" data-testid="fortnight-featured">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <h2 className="text-base font-bold text-zinc-900 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-700" />
                {inWindow ? 'Coming up: Fortnight 1' : `Fortnight ${fortnight} of ${TOTAL_FORTNIGHTS}`}
              </h2>
              <p className="text-xs text-zinc-600 mt-0.5">{fortnightWeekLabel(fortnight)} · <em>{FORTNIGHT_THEMES[fortnight]}</em></p>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white px-2 py-1 rounded-full">5 featured</span>
          </div>
          {inWindow && (
            <p className="text-xs text-zinc-700 mb-3 italic">
              Once you complete the 2-Week Window, your diet journey runs across the 30 Programme Weeks: five fresh recipes every fortnight, building your repertoire across cuisines.
            </p>
          )}
          <p className="text-sm text-zinc-700 leading-relaxed mb-3">
            This fortnight's selection: five recipes designed to fit together as a workable week. Save them to your plan, generate the shopping list and lean on these as the spine of your meals for the next two weeks.
          </p>
          <div className="space-y-2" data-testid="fortnight-meals">
            {featured.map(m => (
              <button
                key={m.id}
                onClick={() => navigate(`/library?tab=diet&meal=${encodeURIComponent(m.id)}`)}
                className="w-full flex items-center gap-3 bg-white hover:bg-zinc-50 border border-zinc-200 rounded-lg p-3 text-left transition-colors"
                data-testid={`fortnight-item-${m.id}`}
              >
                <div className="w-12 h-12 rounded-lg bg-zinc-100 overflow-hidden flex-shrink-0">
                  <img src={imageForMeal(m)} alt={localizedTitle(m, language)} className="w-full h-full object-cover" onError={(e) => { e.currentTarget.src = m.image; }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 truncate">{localizedTitle(m, language)}</div>
                  <div className="text-[11px] text-zinc-500 flex items-center gap-2">
                    <span className="capitalize">{m.category}</span>
                    <span>·</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{m.prep_minutes + m.cook_minutes} min</span>
                    <span>·</span>
                    <span className="flex items-center gap-1 capitalize"><ChefHat className="w-3 h-3" />{m.difficulty}</span>
                  </div>
                </div>
              </button>
            ))}
          </div>
          <p className="text-[11px] text-zinc-500 mt-3 italic">
            New fortnight selections continue every two weeks across the 30 Programme Weeks.
          </p>
        </section>

        {/* Preferences form */}
        <section className="bg-white border border-zinc-200 rounded-xl p-5 mb-5" data-testid="diet-preferences-form">
          <h2 className="text-base font-bold text-zinc-900 mb-1 flex items-center gap-2">
            <Utensils className="w-4 h-4 text-amber-600" />
            Your dietary preferences
          </h2>
          <p className="text-xs text-zinc-500 mb-4">
            Complete this during your 2-week window: it filters the Diet Library so you only see meals that fit you. You can change it any time.
          </p>

          {PREF_GROUPS.map(group => (
            <div key={group.title} className="mb-5">
              <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">{group.title}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {group.options.map(opt => (
                  <button
                    key={opt.key}
                    onClick={() => toggle(opt.key)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg border text-sm text-left transition-colors ${
                      prefs[opt.key] ? 'bg-emerald-50 border-emerald-300 text-emerald-900' : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                    }`}
                    data-testid={`pref-${opt.key}`}
                  >
                    {prefs[opt.key] ? <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0" /> : <span className="w-4 h-4 rounded border border-zinc-300 flex-shrink-0" />}
                    <span>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          {/* Allergies */}
          <div className="mb-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 text-red-500" />
              Allergies and intolerances
            </h3>
            <p className="text-[11px] text-zinc-500 mb-2">Tap a common allergen or type your own and press Enter.</p>

            <div className="flex flex-wrap gap-2 mb-3" data-testid="common-allergies">
              {COMMON_ALLERGIES.map(a => (
                <button
                  key={a}
                  onClick={() => addAllergy(a)}
                  disabled={prefs.allergies.includes(a)}
                  className="text-xs px-2.5 py-1 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  + {a}
                </button>
              ))}
            </div>

            <form onSubmit={(e) => { e.preventDefault(); addAllergy(allergyInput); }} className="flex gap-2 mb-3">
              <input
                type="text"
                value={allergyInput}
                onChange={(e) => setAllergyInput(e.target.value)}
                placeholder="Add custom allergen (e.g. kiwi)"
                className="flex-1 h-10 px-3 rounded-lg border border-zinc-200 text-sm"
                data-testid="allergy-input"
              />
              <button type="submit" className="h-10 px-3 rounded-lg bg-zinc-900 text-white text-sm" data-testid="allergy-add-btn"><Plus className="w-4 h-4" /></button>
            </form>

            {prefs.allergies.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="allergy-chips">
                {prefs.allergies.map(a => (
                  <span key={a} className="inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 text-xs px-2 py-1 rounded-full">
                    {a}
                    <button onClick={() => removeAllergy(a)} className="hover:text-red-900" data-testid={`allergy-remove-${a}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Preferred Cuisines: multi-select, drives the Diet-tab country filter */}
          <div className="mb-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-blue-500" />
              Preferred cuisines
            </h3>
            <p className="text-[11px] text-zinc-500 mb-2">
              Select any cuisines you enjoy. When set, the Diet library will <strong>only</strong> show meals from these cuisines. Leave empty to see all.
            </p>
            <div className="flex flex-wrap gap-2" data-testid="cuisine-options">
              {CUISINE_OPTIONS.map(c => {
                const active = (prefs.cuisines || []).includes(c.id);
                return (
                  <button
                    key={c.id}
                    onClick={() => toggleCuisine(c.id)}
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
                      active
                        ? 'bg-blue-600 text-white border-blue-700'
                        : 'bg-white border-zinc-200 text-zinc-700 hover:border-blue-300'
                    }`}
                    data-testid={`cuisine-${c.id}`}
                  >
                    <span>{c.flag}</span>
                    {c.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Dislikes: free-text foods/ingredients to hide */}
          <div className="mb-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5">
              <X className="w-3.5 h-3.5 text-zinc-500" />
              Foods you dislike
            </h3>
            <p className="text-[11px] text-zinc-500 mb-2">
              Add ingredients or foods you&rsquo;d rather not see (e.g. olives, coriander, mushrooms). We&rsquo;ll hide meals that contain them.
            </p>
            <form onSubmit={(e) => { e.preventDefault(); addDislike(dislikeInput); }} className="flex gap-2 mb-3">
              <input
                type="text"
                value={dislikeInput}
                onChange={(e) => setDislikeInput(e.target.value)}
                placeholder="Add a dislike (e.g. olives)"
                className="flex-1 h-10 px-3 rounded-lg border border-zinc-200 text-sm"
                data-testid="dislike-input"
              />
              <button type="submit" className="h-10 px-3 rounded-lg bg-zinc-900 text-white text-sm" data-testid="dislike-add-btn"><Plus className="w-4 h-4" /></button>
            </form>
            {(prefs.dislikes || []).length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="dislike-chips">
                {(prefs.dislikes || []).map(d => (
                  <span key={d} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 border border-zinc-200 text-xs px-2 py-1 rounded-full">
                    {d}
                    <button onClick={() => removeDislike(d)} className="hover:text-zinc-900" data-testid={`dislike-remove-${d}`}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Spice preference */}
          <div className="mb-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Spice preference</h3>
            <div className="flex gap-2">
              {['mild', 'medium', 'hot'].map(s => (
                <button
                  key={s}
                  onClick={() => setPrefs(p => ({ ...p, spicy_preference: s }))}
                  className={`flex-1 py-2 rounded-lg text-xs font-medium border capitalize ${
                    prefs.spicy_preference === s ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white border-zinc-200 text-zinc-700'
                  }`}
                  data-testid={`spice-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-5">
            <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Other notes (optional)</h3>
            <textarea
              value={prefs.notes}
              onChange={(e) => setPrefs(p => ({ ...p, notes: e.target.value }))}
              rows={3}
              placeholder="Anything else we should consider: e.g. specific medical condition, cooking equipment limitations, family preferences."
              className="w-full p-3 rounded-lg border border-zinc-200 text-sm"
              data-testid="diet-notes"
            />
          </div>

          <button
            onClick={save}
            disabled={saving}
            className="w-full flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-50"
            data-testid="save-prefs-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving…' : (completed ? 'Update preferences' : 'Save preferences')}
          </button>
          {completed && (
            <p className="text-xs text-emerald-700 mt-2 text-center flex items-center justify-center gap-1.5">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Preferences saved: the Diet Library now filters to your needs.
            </p>
          )}
        </section>

        <button
          onClick={() => navigate('/library?tab=diet')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3 text-sm font-semibold"
          data-testid="goto-diet-library"
        >
          Open the Diet Library
        </button>
      </div>
    </div>
  );
}
